import './chat.css';

import { IonButton, IonTextarea, isPlatform } from '@ionic/react';
import React, { ReactElement, forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane } from 'react-icons/fa';
import { ChatMessage } from '../../api/graphql/generated';
import { useCurrentUser } from '../../api/user.hooks';
import { trackCreateChatReply } from '../../config/analytics';
import { isEnter } from '../../util/keyboard';
import { Avatar } from '../avatar/avatar';
import { SaveStatusToast } from '../toast/toast.gql';
import { useChatMessageHelper } from './chat-message.hooks';
import { useUserChatNotificationToastLazy } from './chat.hooks';

interface NewReplyProps {
  // this is the conversation being replied to
  conversation: ChatMessage;
  onFocus?: () => void;
  onBlur?: () => void;
}

const NewReply = forwardRef<HTMLDivElement, NewReplyProps>(
  (props: NewReplyProps, ref): ReactElement => {
    const {
      conversation: { chatId, id: threadId, draft: replyDraft },
      onBlur,
      onFocus,
    } = props;
    const { i18n } = useTranslation();
    const [isFocused, setFocused] = useState<boolean>(false);
    const [messageText, setMessageText] = useState(replyDraft?.message ?? '');
    const user = useCurrentUser();
    const textarea = useRef<HTMLIonTextareaElement>(null);
    const [sendError, setSendError] = useState(false);

    const { present: presentUserChatNotificationToast } =
      useUserChatNotificationToastLazy(chatId);

    const { attachmentList, sendMessage, uploader, validateInput } =
      useChatMessageHelper({
        allowRemove: true,
        chatId,
        threadId: Number(threadId),
        conversationId: replyDraft?.id,
      });

    const isValidInput = validateInput(messageText);

    const onSend = async () => {
      presentUserChatNotificationToast();

      if (textarea.current) {
        const textinput = textarea.current?.value;
        await sendMessage(textinput ?? '', {
          beforeSend: async () => {
            setSendError(false);
          },
          afterSend: async () => {
            trackCreateChatReply();
            if (textarea.current) {
              // reset the message text state so we don't think
              // the input is still valid for sending a message
              setMessageText('');
              textarea.current.value = null;
            }
          },
          onError: () => {
            setSendError(true);
          },
        });
      }
    };

    const submitIfEnter = (e: React.KeyboardEvent<HTMLIonTextareaElement>) => {
      if (shouldSendOnEnter(e)) {
        if (isValidInput) {
          onSend();
        }
      }
    };

    const classList: string[] = ['chat-message-input-container'];

    if (!isValidInput && !isFocused) {
      classList.push('chat-message-input-blurred');
    }

    return (
      <div
        ref={ref}
        className={classList.join(' ')}
        onClick={(e) => {
          e.stopPropagation();
          textarea.current?.setFocus();
        }}
      >
        <Avatar
          attachment={user?.avatarAttachment}
          avatarProps={{
            className: 'chat-avatar',
          }}
        />
        <div className="chat-message-textarea">
          <IonTextarea
            className="ion-no-padding ion-no-margin"
            placeholder={i18n.t('common:chat_add_comment')}
            autoGrow={true}
            autocapitalize="on"
            rows={1}
            ref={textarea}
            onFocus={() => {
              if (onFocus) {
                onFocus();
              }
              setFocused(true);
            }}
            onKeyDown={submitIfEnter}
            onBlur={() => {
              if (onBlur) {
                onBlur();
              }
              setFocused(false);
            }}
            onIonInput={(e) => {
              setMessageText(e.detail.value ?? '');
            }}
          />
        </div>
        <div className="chat-message-action-buttons">
          {uploader}
          {isValidInput && (
            <IonButton
              fill="clear"
              size="small"
              disabled={!isValidInput}
              onClick={onSend}
            >
              <FaPaperPlane
                className={
                  isValidInput ? 'send-icon-valid' : 'send-icon-invalid'
                }
                size={18}
              />
            </IonButton>
          )}
        </div>
        <div className="chat-message-attachments">{attachmentList}</div>
        <SaveStatusToast
          error={sendError}
          defaultErrorMessageKey="common:chat_create_error"
          duration={3000}
        />
      </div>
    );
  }
);

const shouldSendOnEnter = (
  e: React.KeyboardEvent<HTMLIonTextareaElement>
): boolean => {
  return isEnter(e) && !isPlatform('capacitor');
};

NewReply.displayName = 'NewReply';

export { NewReply };
